import { createContext, useContext, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import IKhcHub from 'utils/khc-hub';
import tr from '../components/translate/tr.json';
import PropTypes from 'prop-types';
import dayjs from 'utils/dayjs';

function enhanceSwalAccessibility(popup, { title, text, html, icon }) {
  const isAlertDialog = icon === 'warning' || icon === 'error';

  popup.setAttribute('role', isAlertDialog ? 'alertdialog' : 'dialog');
  popup.setAttribute('aria-modal', 'true');
  popup.setAttribute('tabindex', '-1');

  const titleEl = popup.querySelector('.swal2-title');
  const contentEl =
    popup.querySelector('.swal2-html-container') || popup.querySelector('#swal2-content');

  const labelledByIds = [];

  if (titleEl && title) {
    titleEl.id = 'swal-dialog-title';
    labelledByIds.push('swal-dialog-title');
  }

  if (contentEl && (text || html)) {
    contentEl.id = 'swal-dialog-desc';
    labelledByIds.push('swal-dialog-desc');
  }

  if (labelledByIds.length > 0) {
    popup.setAttribute('aria-labelledby', labelledByIds.join(' '));
  } else {
    popup.removeAttribute('aria-labelledby');
  }

  popup.removeAttribute('aria-describedby');

  const iconEl = popup.querySelector('.swal2-icon');
  if (iconEl) {
    iconEl.setAttribute('aria-hidden', 'true');
  }
}

const toastOpts = {
  position: 'bottom-center',
  autoClose: 5000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  pauseOnFocusLoss: true,
  draggable: false,
  progress: undefined,
  theme: 'colored',
  role: 'alert',
};

export const AppContext = createContext();

export function AppContextProvider({ children }) {
  const intl = useIntl();
  const khcHubRef = useRef(null);
  const dialogTriggerRef = useRef(null);
  const [redirect, setRedirect] = useState(null);

  const contextValue = useMemo(() => {
    const baseTimezone = 'Europe/Istanbul';
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const userUtcGmt = dayjs().format('Z');

    return {
      translate: (e, values = {}) => {
        return intl.formatMessage({ id: e, defaultMessage: e }, values);
      },
      translateForAnalytics: e => {
        return tr[e] ? tr[e] : e;
      },
      dateFormat: (date, format = 'DD.MM.YYYY') => {
        return dayjs(date).format(format);
      },
      toastError: e => {
        toast.error(e, toastOpts);
      },
      toastWarning: e => {
        toast.warning(e, toastOpts);
      },
      toastSuccess: e => {
        toast.success(e, toastOpts);
      },
      showDialog: (
        title,
        text,
        icon,
        showCancelButton,
        confirmButtonText,
        cancelButtonText,
        onConfirm = () => {},
        onCancel = () => {},
        html,
        cancelButtonCss = 'btn btn-danger',
        showDenyButton = false,
        denyButtonText = 'Deny',
        onDeny = () => {}
      ) => {
        dialogTriggerRef.current = document.activeElement;

        const isAlertDialog = icon === 'warning' || icon === 'error';
        const shouldFocusCancel = isAlertDialog && showCancelButton;

        Swal.fire({
          title: title,
          text: text,
          icon: icon,
          showDenyButton: showDenyButton,
          showCancelButton: showCancelButton,
          confirmButtonText: confirmButtonText,
          cancelButtonText: cancelButtonText,
          html: html,
          denyButtonText: denyButtonText,
          reverseButtons: true,
          focusConfirm: !shouldFocusCancel,
          focusCancel: shouldFocusCancel,
          returnFocus: true,
          allowEscapeKey: true,
          customClass: {
            confirmButton: 'btn btn-success',
            cancelButton: cancelButtonCss,
            denyButton: 'btn btn-success',
          },
          buttonsStyling: false,
          didOpen: popup => {
            enhanceSwalAccessibility(popup, { title, text, html, icon });
          },
          didClose: () => {
            const trigger = dialogTriggerRef.current;
            dialogTriggerRef.current = null;

            requestAnimationFrame(() => {
              if (trigger && document.body.contains(trigger)) {
                trigger.focus();
                return;
              }

              const asideCloseBtn = document.querySelector('.aside-overlay .btn-close');
              if (asideCloseBtn) {
                asideCloseBtn.focus();
              }
            });
          },
        }).then(result => {
          if (result.isConfirmed) {
            onConfirm();
          } else if (result.isDenied) {
            onDeny();
          } else {
            onCancel();
          }
        });
      },
      initKhcSocket: () => {
        khcHubRef.current = new IKhcHub(intl);
        khcHubRef.current.Connect();
      },
      disconnectKhcSocket: () => {
        if (khcHubRef.current) {
          khcHubRef.current.Disconnect();
        }
      },
      baseTimezone,
      userTimezone,
      userUtcGmt,
      redirect,
      setRedirect: data => {
        setRedirect(data);
      },

      // farkli zaman dilimlerindeki kullanicilar icin tarih ve saat gosterimleri bu fonksiyonlarla yapilir
      formatDateTimeByTimezone: (date, format = 'DD MMMM YYYY, dddd HH:mm') => {
        return dayjs.tz(date, baseTimezone).tz(userTimezone).format(format);
      },
      formatDateByTimezone: (date, format = 'DD MMMM YYYY, dddd') => {
        return dayjs.tz(date, baseTimezone).tz(userTimezone).format(format);
      },
      formatTimeByTimezone: (date, format = 'HH:mm') => {
        return dayjs.tz(date, baseTimezone).tz(userTimezone).format(format);
      },
      getDayjsByTimezone: date => {
        return dayjs.tz(date, baseTimezone).tz(userTimezone);
      },
      getRemainingTime: date => {
        const targetDate = dayjs.tz(date, baseTimezone).tz(userTimezone);
        const now = dayjs().tz(userTimezone);
        const duration = dayjs.duration(targetDate.diff(now));

        return {
          days: Math.floor(duration.asDays()),
          hours: duration.hours(),
          mins: duration.minutes(),
        };
      },
      formatPrice: (price, currencySign = '') => {
        const formatted = new Intl.NumberFormat(intl.locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(price);

        return currencySign ? `${formatted} ${currencySign}` : formatted;
      },
    };
  }, [intl, redirect]);

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
}

export const useAppContext = () => {
  return useContext(AppContext);
};

AppContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
